"use client";

import {
  ComposerPrimitive,
  MessagePrimitive,
  ThreadPrimitive,
  useMessage,
} from "@assistant-ui/react";
import { ComposerAddAttachment, ComposerAttachments, UserMessageAttachments } from "../assistant-ui/attachment";
import { PaperPlaneIcon } from "@radix-ui/react-icons";
import { RefreshCwIcon, WifiIcon, WifiOffIcon, ZapIcon, ZapOffIcon, SquareIcon } from "lucide-react";
import type { FC, ReactNode } from "react";
import React, { createContext, useContext, useMemo, useState, useCallback, useEffect } from "react";
import { cn } from "@/lib/utils";
import { ExecutionLog, type ExecutionEvent } from "../assistant-ui/execution-log";
import { MarkdownText } from "../assistant-ui/markdown-text";
import { ResourceFormWrapper, isResourceFormMessage } from "../assistant-ui/resource-form-message";
import { ResourceFormRenderer, mcpSchemaToFormSchema, cleanFormValues, buildDefaultValues } from "../resource-form";
import type { ResourceSchema } from "../resource-form/types";
import type { MessageMetadata } from "@/lib/useConversation";
import type { WaitingForUserInput } from "@/lib/useEurekaRuntime";
import { AgentSearchDropdown, type CatalogItem } from "../assistant-ui/agent-search-dropdown";

interface ClaudeProps {
  executionEvents?: ExecutionEvent[];
  isRunning?: boolean;
  isConnected?: boolean;  // WebSocket connection status
  onInjectMockEvents?: () => void;
  onInjectTestMessage?: (content: string) => void;  // For testing UI components
  messageMetadata?: Map<string, MessageMetadata>;  // Map of messageId -> metadata
  waitingForUserInput?: WaitingForUserInput | null;  // ask_user: current question
  onSubmitUserResponse?: (response: string) => void;  // ask_user: submit response
  onSendMessage?: (message: string) => void;  // Send new message (for form submissions)
  onReconnect?: () => void;  // Manual WebSocket reconnect
  onCancel?: () => void;  // Stop running agent execution
  // @ Command feature (local catalog search)
  enableAtCommands?: boolean;  // Enable @ commands for catalog search
  /** Local catalog for @ search (agents and skills) */
  searchCatalog?: CatalogItem[];
  /** @deprecated Use searchCatalog prop instead */
  searchEndpoint?: string;
}

// Context to pass execution events to message components
const ExecutionEventsContext = createContext<{
  events: ExecutionEvent[];
  isRunning: boolean;
  messageMetadata: Map<string, MessageMetadata>;
  waitingForUserInput: WaitingForUserInput | null;
  onSubmitUserResponse?: (response: string) => void;
  onSendMessage?: (message: string) => void;
  powerMode: boolean;
  setPowerMode: (value: boolean) => void;
}>({ events: [], isRunning: false, messageMetadata: new Map(), waitingForUserInput: null, powerMode: false, setPowerMode: () => {} });

/**
 * Terminal-style chat interface
 * Unified dark theme with flowing log-style messages
 */
export const Claude: FC<ClaudeProps> = ({
  executionEvents = [],
  isRunning = false,
  isConnected = false,
  onInjectMockEvents,
  onInjectTestMessage,
  messageMetadata = new Map(),
  waitingForUserInput = null,
  onSubmitUserResponse,
  onSendMessage,
  onReconnect,
  onCancel,
  enableAtCommands = false,
  searchCatalog = [],
  searchEndpoint,
}) => {
  // Power Mode state - persisted in localStorage (default: true)
  const [powerMode, setPowerMode] = useState(() => {
    if (typeof window !== 'undefined') {
      const stored = localStorage.getItem('powerMode');
      // Default to true if not explicitly set to 'false'
      return stored !== 'false';
    }
    return true;
  });

  const handleSetPowerMode = useCallback((value: boolean) => {
    setPowerMode(value);
    if (typeof window !== 'undefined') {
      localStorage.setItem('powerMode', String(value));
    }
  }, []);

  return (
    <ExecutionEventsContext.Provider value={{
      events: executionEvents,
      isRunning,
      messageMetadata,
      waitingForUserInput,
      onSubmitUserResponse,
      onSendMessage,
      powerMode,
      setPowerMode: handleSetPowerMode,
    }}>
      <ThreadPrimitive.Root className="flex h-full w-full max-w-full flex-col items-stretch bg-background font-mono text-sm min-w-0 overflow-hidden">

        <ThreadPrimitive.Viewport data-chat-viewport className="flex flex-1 min-h-0 flex-col overflow-y-auto overflow-x-hidden px-3 py-2 min-w-0 max-w-full">
          {/* Welcome message when empty */}
          <ThreadPrimitive.If empty>
            <div className="flex flex-col items-center justify-center h-full text-muted-foreground">
              <div className="text-lg mb-2">Eureka Agent Runtime</div>
              <div className="text-xs">Type a message to start an agent execution</div>
            </div>
          </ThreadPrimitive.If>

          {/* Message thread - each message renders its own execution events inline */}
          <ThreadPrimitive.Messages components={{ Message: TerminalMessage }} />
        </ThreadPrimitive.Viewport>

        {/* Unified Input Area - handles both chat and ask_user */}
        <UnifiedInputArea
          waitingForUserInput={waitingForUserInput}
          onSubmitUserResponse={onSubmitUserResponse}
          isRunning={isRunning}
          isConnected={isConnected}
          onReconnect={onReconnect}
          onCancel={onCancel}
          enableAtCommands={enableAtCommands}
          searchCatalog={searchCatalog}
        />
      </ThreadPrimitive.Root>
    </ExecutionEventsContext.Provider>
  );
};

/**
 * Unified Input Area - Integrates chat input with ask_user responses
 * Shows question banner above input when agent is waiting for user response
 * Supports @ commands for catalog search when enableAtCommands is true
 */
const UnifiedInputArea: FC<{
  waitingForUserInput: WaitingForUserInput | null;
  onSubmitUserResponse?: (response: string) => void;
  isRunning: boolean;
  isConnected: boolean;
  onReconnect?: () => void;
  onCancel?: () => void;
  enableAtCommands?: boolean;
  searchCatalog?: CatalogItem[];
}> = ({ waitingForUserInput, onSubmitUserResponse, isRunning, isConnected, onReconnect, onCancel, enableAtCommands = false, searchCatalog = [] }) => {
  const { powerMode, setPowerMode } = useContext(ExecutionEventsContext);
  const [selectedOption, setSelectedOption] = useState<string | null>(null);
  const [selectedOptions, setSelectedOptions] = useState<Set<string>>(new Set());
  const [isFormLoading, setIsFormLoading] = useState(false);

  // @ and # Command state (unified search)
  const [showSearch, setShowSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const isAskingUser = !!waitingForUserInput;
  const inputType = waitingForUserInput?.inputType;
  const options = waitingForUserInput?.options;
  const formSchema = waitingForUserInput?.formSchema;
  const formValues = waitingForUserInput?.formValues;
  const formAction = waitingForUserInput?.formAction;
  const resourceType = waitingForUserInput?.resourceType;
  const resourceId = waitingForUserInput?.resourceId;

  // Debug logging
  console.log('🎯 UnifiedInputArea state:', {
    isAskingUser,
    inputType,
    hasFormSchema: !!formSchema,
    formAction,
    enableAtCommands,
    searchCatalogLength: searchCatalog?.length || 0,
    waitingForUserInput: waitingForUserInput ? JSON.stringify(waitingForUserInput).slice(0, 500) : null
  });

  // Reset selections when ask_user changes
  React.useEffect(() => {
    setSelectedOption(null);
    setSelectedOptions(new Set());
  }, [waitingForUserInput]);

  // Handle option click for single_select - submit immediately
  const handleOptionClick = (option: string) => {
    if (inputType === 'single_select' && onSubmitUserResponse) {
      onSubmitUserResponse(option);
    } else if (inputType === 'multi_select') {
      const newSet = new Set(selectedOptions);
      if (newSet.has(option)) {
        newSet.delete(option);
      } else {
        newSet.add(option);
      }
      setSelectedOptions(newSet);
    }
  };

  // Handle confirmation buttons - submit immediately
  const handleConfirmation = (value: string) => {
    if (onSubmitUserResponse) {
      onSubmitUserResponse(value);
    }
  };

  // Handle resource form submission
  const handleFormSubmit = useCallback(
    async (values: Record<string, unknown>) => {
      setIsFormLoading(true);
      try {
        const cleanedValues = cleanFormValues(values);
        const response = JSON.stringify({
          action: formAction,
          resource_type: resourceType,
          resource_id: resourceId,
          values: cleanedValues,
        });
        onSubmitUserResponse?.(response);
      } finally {
        setIsFormLoading(false);
      }
    },
    [formAction, resourceType, resourceId, onSubmitUserResponse]
  );

  // Handle form cancel
  const handleFormCancel = useCallback(() => {
    onSubmitUserResponse?.(JSON.stringify({ action: "cancel" }));
  }, [onSubmitUserResponse]);

  // Convert MCP schema to form schema
  const convertedSchema: ResourceSchema | null = useMemo(() => {
    console.log('🔍 UnifiedInputArea - inputType:', inputType, 'formSchema:', formSchema);
    if (inputType === 'resource_form' && formSchema) {
      try {
        const converted = mcpSchemaToFormSchema(formSchema as never);
        console.log('✅ Converted schema:', converted);
        return converted;
      } catch (e) {
        console.error('❌ Failed to convert schema:', e);
        return null;
      }
    }
    return null;
  }, [inputType, formSchema]);

  // Build initial values from schema defaults (includes pre-selected table rows)
  const formInitialValues = useMemo(() => {
    if (convertedSchema) {
      const defaults = buildDefaultValues(convertedSchema);
      // Merge with any explicit formValues from backend
      return { ...defaults, ...(formValues || {}) };
    }
    return formValues || {};
  }, [convertedSchema, formValues]);

  // Calculate time remaining
  const timeRemaining = waitingForUserInput?.timeoutAt
    ? getTimeRemaining(waitingForUserInput.timeoutAt)
    : null;

  // Get placeholder text
  const getPlaceholder = () => {
    if (!isAskingUser) return "Enter your goal...";
    if (inputType === 'confirmation') return "Or type your response...";
    if (inputType === 'single_select' || inputType === 'multi_select') return "Or type custom response...";
    return "Type your response...";
  };

  return (
    <div className="border-t border-border bg-card px-2 py-1.5">
      {/* Question Banner - shown when ask_user is active */}
      {isAskingUser && waitingForUserInput && (
        <div className="mb-2 p-2 bg-muted/50 border border-amber-500/30 rounded-lg">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <span className="text-amber-400 text-xs font-semibold flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
              INPUT REQUIRED
            </span>
            {timeRemaining && (
              <span className="text-muted-foreground text-[10px]">expires {timeRemaining}</span>
            )}
          </div>

          {/* Question */}
          <div className="text-foreground text-sm mb-3">
            {waitingForUserInput.question}
          </div>

          {/* Options for single_select / multi_select */}
          {(inputType === 'single_select' || inputType === 'multi_select') && options && (
            <div className="flex flex-wrap gap-2">
              {options.map((option) => {
                const isSelected = inputType === 'multi_select'
                  ? selectedOptions.has(option)
                  : selectedOption === option;
                return (
                  <button
                    key={option}
                    onClick={() => handleOptionClick(option)}
                    className={cn(
                      "px-3 py-1.5 rounded text-xs font-medium transition-colors",
                      isSelected
                        ? "bg-amber-500 text-white"
                        : "bg-muted text-foreground hover:bg-muted/80"
                    )}
                  >
                    {inputType === 'multi_select' && (
                      <span className="mr-1">{isSelected ? '☑' : '☐'}</span>
                    )}
                    {option}
                  </button>
                );
              })}
              {/* Submit button for multi_select */}
              {inputType === 'multi_select' && selectedOptions.size > 0 && (
                <button
                  onClick={() => {
                    if (onSubmitUserResponse) {
                      onSubmitUserResponse(Array.from(selectedOptions).join(', '));
                    }
                  }}
                  className="px-3 py-1.5 rounded text-xs font-medium bg-emerald-600 text-white hover:bg-emerald-500"
                >
                  Submit ({selectedOptions.size})
                </button>
              )}
            </div>
          )}

          {/* Confirmation buttons */}
          {inputType === 'confirmation' && (
            <div className="flex gap-2">
              <button
                onClick={() => handleConfirmation('Yes')}
                className="px-4 py-1.5 rounded text-sm font-medium bg-emerald-600 text-white hover:bg-emerald-500 transition-colors"
              >
                ✓ Yes
              </button>
              <button
                onClick={() => handleConfirmation('No')}
                className="px-4 py-1.5 rounded text-sm font-medium bg-muted text-foreground hover:bg-red-600 hover:text-white transition-colors"
              >
                ✗ No
              </button>
            </div>
          )}

          {/* Resource Form */}
          {inputType === 'resource_form' && (
            <div className="mt-2 max-h-[60vh] overflow-y-auto">
              {convertedSchema ? (
                <ResourceFormRenderer
                  schema={convertedSchema}
                  initialValues={formInitialValues as Record<string, unknown>}
                  onSubmit={handleFormSubmit}
                  onCancel={handleFormCancel}
                  mode={formAction === "view" ? "view" : formAction || "create"}
                  isLoading={isFormLoading}
                />
              ) : (
                <div className="text-red-400 text-xs p-2 border border-red-500/30 rounded">
                  Failed to render form. Schema: {formSchema ? 'present' : 'missing'}
                  <pre className="mt-2 text-[10px] overflow-auto max-h-32">
                    {JSON.stringify(formSchema, null, 2)}
                  </pre>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* Compact Input Field */}
      {isAskingUser ? (
        // Ask user active - standalone input that submits to onSubmitUserResponse
        <div className="flex items-center gap-1">
          <span className="font-bold text-amber-400 text-xs">❯</span>
          <AskUserTextInput
            placeholder={getPlaceholder()}
            onSubmit={onSubmitUserResponse}
          />
        </div>
      ) : (
        // Normal chat input with @ command support
        <ComposerPrimitive.Root className="flex flex-col gap-1">
          {/* Attachments preview */}
          <ComposerAttachments />

          {/* Input row with @ command dropdown */}
          <div className="relative flex items-center gap-1">
            <span className="font-bold text-emerald-400 text-xs">❯</span>
            <ComposerAddAttachment />
            <div className="relative flex-grow">
              <ComposerPrimitive.Input
                placeholder={enableAtCommands ? "Type a message... Use @ to mention skills" : getPlaceholder()}
                className="w-full bg-transparent text-foreground text-sm outline-none placeholder:text-muted-foreground"
                onChange={(e) => {
                  const value = e.target.value;
                  // Check for @ command (catalog search)
                  if (enableAtCommands && value.startsWith('@')) {
                    setShowSearch(true);
                    setSearchQuery(value);
                  } else {
                    setShowSearch(false);
                    setSearchQuery('');
                  }
                }}
              />
              {/* Search Dropdown for @ commands - uses local catalog */}
              {enableAtCommands && showSearch && (
                <AgentSearchDropdown
                  query={searchQuery}
                  catalog={searchCatalog}
                  onSelect={(agent) => {
                    // Put selected item in input, user sends manually
                    const message = `@${agent.name} `;
                    const input = document.querySelector('textarea') as HTMLTextAreaElement;
                    if (input) {
                      const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
                      nativeInputValueSetter?.call(input, message);
                      input.dispatchEvent(new Event('input', { bubbles: true }));
                      input.focus();
                    }
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  onClose={() => {
                    setShowSearch(false);
                    setSearchQuery('');
                  }}
                  onSubmitWithoutSelect={() => {
                    setShowSearch(false);
                  }}
                />
              )}
            </div>
            {isRunning && onCancel ? (
              <button
                type="button"
                onClick={onCancel}
                className="flex h-5 w-5 items-center justify-center rounded bg-red-600 text-white hover:bg-red-500 transition-colors"
                title="Stop execution"
              >
                <SquareIcon width={10} height={10} fill="currentColor" />
              </button>
            ) : (
              <ComposerPrimitive.Send
                type="submit"
                className="flex h-5 w-5 items-center justify-center rounded bg-emerald-600 text-white hover:bg-emerald-500 disabled:opacity-30 disabled:hover:bg-emerald-600 transition-colors"
              >
                <PaperPlaneIcon width={10} height={10} />
              </ComposerPrimitive.Send>
            )}
          </div>
        </ComposerPrimitive.Root>
      )}

      {/* Compact Status Line */}
      <div className="mt-1 flex items-center justify-between text-[9px] text-muted-foreground">
        <div className="flex items-center gap-1.5">
          {/* Power Mode Toggle */}
          <button
            onClick={() => setPowerMode(!powerMode)}
            className={cn(
              "flex items-center gap-0.5 px-1 py-0.5 rounded transition-colors",
              powerMode
                ? "text-amber-400 bg-amber-500/20 hover:bg-amber-500/30"
                : "text-muted-foreground hover:text-foreground hover:bg-muted"
            )}
            title={powerMode ? "Power Mode ON" : "Power Mode OFF"}
          >
            {powerMode ? <ZapIcon className="w-2.5 h-2.5" /> : <ZapOffIcon className="w-2.5 h-2.5" />}
          </button>
        </div>
        <div className="flex items-center gap-1.5">
          {isAskingUser ? (
            <span className="flex items-center gap-0.5 text-amber-400">
              <span className="inline-block w-1 h-1 rounded-full bg-amber-400 animate-pulse" />
              waiting
            </span>
          ) : isRunning ? (
            <span className="flex items-center gap-0.5 text-emerald-400">
              <span className="inline-block w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
              running
            </span>
          ) : isConnected ? (
            <span className="flex items-center gap-0.5 text-emerald-400">
              <WifiIcon className="w-2.5 h-2.5" />
            </span>
          ) : (
            <span className="flex items-center gap-0.5 text-muted-foreground">
              <WifiOffIcon className="w-2.5 h-2.5" />
            </span>
          )}
          {onReconnect && !isRunning && !isAskingUser && (
            <button
              onClick={onReconnect}
              className={cn(
                "flex items-center gap-0.5 px-1 py-0.5 rounded transition-colors",
                isConnected
                  ? "text-muted-foreground hover:bg-muted"
                  : "text-amber-400 hover:text-amber-300 hover:bg-muted"
              )}
              title="Refresh"
            >
              <RefreshCwIcon className="w-2.5 h-2.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

/**
 * Custom text input for ask_user free_text that submits to onSubmitUserResponse
 */
const AskUserTextInput: FC<{
  placeholder: string;
  onSubmit?: (response: string) => void;
}> = ({ placeholder, onSubmit }) => {
  const [value, setValue] = useState('');

  const handleSubmit = () => {
    if (value.trim() && onSubmit) {
      onSubmit(value.trim());
      setValue('');
    }
  };

  return (
    <>
      <input
        type="text"
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' && value.trim()) {
            handleSubmit();
          }
        }}
        placeholder={placeholder}
        className="flex-grow bg-transparent text-foreground text-sm outline-none placeholder:text-muted-foreground"
        autoFocus
      />
      <button
        onClick={handleSubmit}
        disabled={!value.trim()}
        className="flex h-5 w-5 items-center justify-center rounded bg-amber-500 text-background hover:bg-amber-400 disabled:opacity-30 disabled:hover:bg-amber-500 transition-colors"
      >
        <PaperPlaneIcon width={10} height={10} />
      </button>
    </>
  );
};

/**
 * Calculate human-readable time remaining
 */
function getTimeRemaining(timeoutAt: string): string {
  const now = new Date();
  const timeout = new Date(timeoutAt);
  const diffMs = timeout.getTime() - now.getTime();

  if (diffMs <= 0) return "expired";

  const diffSec = Math.floor(diffMs / 1000);
  if (diffSec < 60) return `${diffSec}s`;

  const diffMin = Math.floor(diffSec / 60);
  if (diffMin < 60) return `${diffMin}m`;

  const diffHour = Math.floor(diffMin / 60);
  return `${diffHour}h ${diffMin % 60}m`;
}

/**
 * Terminal-style message component
 * Renders user messages and assistant execution logs inline
 * Uses unified executionEvents from runtime - works for both live and historical
 */
const TerminalMessage: FC = () => {
  const message = useMessage();
  const { events, isRunning, messageMetadata, waitingForUserInput, onSendMessage, powerMode } = useContext(ExecutionEventsContext);

  // DEBUG: Log message content
  console.log('🔍 TerminalMessage render - message.id:', message.id, 'role:', message.role, 'content:', JSON.stringify(message.content)?.substring(0, 200));

  // Filter events for this specific assistant message
  const messageEvents = useMemo(() => {
    if (message.role !== 'assistant') return [];
    const filtered = events.filter(e => e.messageId === message.id);
    console.log('🔍 TerminalMessage filter - message.id:', message.id, 'total events:', events.length, 'matched:', filtered.length, 'event messageIds:', [...new Set(events.map(e => e.messageId))]);
    return filtered;
  }, [events, message.id, message.role]);

  // Check if this is the last assistant message (for running/waiting indicator)
  const isLastAssistant = message.role === 'assistant' && message.isLast;
  const showRunning = isLastAssistant && isRunning;
  const showWaiting = isLastAssistant && !!waitingForUserInput;

  // DEBUG: Log which path we're taking
  console.log('🔍 TerminalMessage path - eventsLen:', messageEvents.length, 'showRunning:', showRunning, 'showWaiting:', showWaiting, 'willShowHistorical:', !(messageEvents.length > 0 || showRunning || showWaiting));

  // Check if we need to show message text content directly.
  // ExecutionLog shows text from llm_end events, but if execution failed,
  // there won't be llm_end events with text - only the error we added to message content.
  // So we show message text content only when there are no llm_end events with text.
  const hasLLMTextInEvents = useMemo(() => {
    return messageEvents.some(e =>
      e.type === 'llm_end' &&
      Array.isArray((e as any).content) &&
      (e as any).content.some((c: any) => c.type === 'text' && c.text?.trim())
    );
  }, [messageEvents]);

  const messageTextContent = useMemo(() => {
    if (hasLLMTextInEvents) return null; // Already shown via ExecutionLog
    if (!Array.isArray(message.content)) return null;
    const textParts = message.content.filter(p => p.type === 'text' && p.text?.trim());
    return textParts.length > 0 ? textParts : null;
  }, [message.content, hasLLMTextInEvents]);

  return (
    <MessagePrimitive.Root>
      <MessagePrimitive.If user>
        <UserMessageContent />
      </MessagePrimitive.If>
      <MessagePrimitive.If assistant>
        {/* Assistant response - left aligned, full width, with border */}
        <div className="mb-3 w-full">
          <div
            className="bg-card border border-border text-left shadow-sm"
            style={{
              borderRadius: '12px',
              borderBottomLeftRadius: '3px',
              padding: '10px 14px',
            }}
          >
            {(messageEvents.length > 0 || showRunning || showWaiting) ? (
              <>
                <ExecutionLog events={messageEvents} isRunning={showRunning} isWaiting={showWaiting} onFormSubmit={onSendMessage} powerMode={powerMode} />
                {/* Show message text content when not already shown via llm_end events (e.g., errors) */}
                {messageTextContent && !showRunning && (
                  <div className="mt-2 border-t border-border pt-2">
                    {messageTextContent.map((part, idx) => (
                      <div key={idx} className="text-red-400">
                        <MarkdownText text={part.text || ''} />
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <HistoricalAssistantMessage metadata={messageMetadata.get(message.id)} />
            )}
          </div>
        </div>
      </MessagePrimitive.If>
    </MessagePrimitive.Root>
  );
};

/**
 * Format timestamp to user's local time
 */
function formatMessageTime(date: Date | undefined): string {
  if (!date) return '';
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

/**
 * User message content - styled as a right-aligned chat bubble
 */
const UserMessageContent: FC = () => {
  const message = useMessage();
  const timestamp = message.createdAt ? formatMessageTime(message.createdAt) : '';

  return (
    <div className="mb-3 w-full">
      {/* Attachments - right aligned */}
      <div className="flex justify-end mb-1">
        <UserMessageAttachments />
      </div>

      {/* Message bubble - right aligned, max 75% width */}
      <div className="flex justify-end">
        <div
          className="text-white font-mono text-sm leading-relaxed inline-block dark:bg-slate-700 bg-primary border border-border/20 shadow-sm"
          style={{
            padding: '10px 14px',
            borderRadius: '12px',
            borderBottomRightRadius: '3px',
            maxWidth: '75%',
            wordBreak: 'break-word',
          }}
        >
          <MessagePrimitive.Parts components={{ Text: PlainText }} />
        </div>
      </div>

      {/* Timestamp - right aligned */}
      {timestamp && (
        <div className="flex justify-end mt-1">
          <span className="text-muted-foreground text-[10px] mr-1">{timestamp}</span>
        </div>
      )}
    </div>
  );
};

/**
 * Plain text for user messages
 */
const PlainText: FC<{ text: string }> = ({ text }) => {
  return <span>{text}</span>;
};

/**
 * Historical assistant message - renders in same style as ExecutionLog
 * Uses useMessage() to access full message content including metrics
 */
const HistoricalAssistantMessage: FC<{ metadata?: MessageMetadata }> = ({ metadata }) => {
  const message = useMessage();
  const { onSubmitUserResponse } = useContext(ExecutionEventsContext);

  // Extract content parts from the message
  const content = message.content;
  if (!content || (Array.isArray(content) && content.length === 0)) {
    return null;
  }

  // Use the metadata passed via props (from context lookup by message ID)
  const { input_tokens, output_tokens, cost_usd, duration_ms, model } = metadata || {};

  return (
    <div className="font-mono text-sm space-y-1 text-foreground text-left">
      {Array.isArray(content) ? (
        content.map((part, idx) => {
          if (part.type === 'text') {
            return (
              <HistoricalTextPart
                key={idx}
                text={part.text}
                inputTokens={input_tokens}
                outputTokens={output_tokens}
                costUsd={cost_usd}
                durationMs={duration_ms}
                model={model}
                onSubmitResponse={onSubmitUserResponse}
              />
            );
          } else if (part.type === 'tool-call') {
            return (
              <HistoricalToolPart
                key={idx}
                toolName={part.toolName}
                result={part.result}
                isError={part.isError}
                durationMs={duration_ms}
              />
            );
          }
          return null;
        })
      ) : (
        <HistoricalTextPart text={String(content)} onSubmitResponse={onSubmitUserResponse} />
      )}
    </div>
  );
};

/**
 * Historical text part - matches LLMResponseEntry style with metrics
 * Also handles resource_form messages by rendering ResourceFormWrapper
 */
const HistoricalTextPart: FC<{
  text: string;
  inputTokens?: number;
  outputTokens?: number;
  costUsd?: number;
  durationMs?: number;
  model?: string;
  onSubmitResponse?: (response: string) => void;
}> = ({ text, inputTokens, outputTokens, costUsd, durationMs, onSubmitResponse }) => {
  if (!text.trim()) return null;

  const hasMetrics = inputTokens || outputTokens || costUsd || durationMs;

  // Check if this is a resource form message
  const isFormMessage = isResourceFormMessage(text);

  // Check if this is a welcome message (starts with emoji + "Hello! I'm")
  const isWelcomeMessage = /^.{1,2}\s*\*\*Hello! I'm/.test(text);

  // Welcome messages get special styling - no header, just the content
  if (isWelcomeMessage) {
    return (
      <div className="border-l-2 border-emerald-400 pl-3 py-2 bg-emerald-950/30 rounded-r">
        <SimpleMarkdown text={text} className="text-emerald-100" />
      </div>
    );
  }

  return (
    <div className="border-l-2 border-emerald-500 pl-3 py-1">
      <div className="flex items-center gap-2 text-emerald-400 flex-wrap">
        <span className="font-semibold">{isFormMessage ? "FORM" : "LLM"}</span>
        {hasMetrics ? (
          <>
            {durationMs && (
              <>
                <span className="text-muted-foreground">|</span>
                <span className="text-muted-foreground">{Math.round(durationMs)}ms</span>
              </>
            )}
            {(inputTokens || outputTokens) && (
              <>
                <span className="text-muted-foreground">|</span>
                <span className="text-amber-400">{inputTokens || 0}+{outputTokens || 0} tok</span>
              </>
            )}
            {costUsd && (
              <>
                <span className="text-muted-foreground">|</span>
                <span className="text-green-400">${costUsd.toFixed(4)}</span>
              </>
            )}
          </>
        ) : (
          <>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">restored</span>
          </>
        )}
      </div>
      <div className="mt-1">
        <ResourceFormWrapper text={text} onSubmitResponse={onSubmitResponse} />
      </div>
    </div>
  );
};

/**
 * Historical tool part - matches ToolResultEntry style
 */
const HistoricalToolPart: FC<{
  toolName: string;
  result?: unknown;
  isError?: boolean;
  durationMs?: number;
}> = ({ toolName, result, isError, durationMs }) => {
  return (
    <div className={`border-l-2 pl-3 py-1 ${isError ? "border-red-500" : "border-cyan-500"}`}>
      <div className={`flex items-center gap-2 ${isError ? "text-red-400" : "text-cyan-400"}`}>
        <span className="font-semibold">{result !== undefined ? "RESULT" : "TOOL"}</span>
        <span className="text-muted-foreground">{toolName}</span>
        {durationMs && (
          <>
            <span className="text-muted-foreground">|</span>
            <span className="text-muted-foreground">{Math.round(durationMs)}ms</span>
          </>
        )}
        {isError && <span className="text-red-400">[error]</span>}
      </div>
      {result !== undefined && (
        <div className="mt-1 ml-2 text-muted-foreground text-[10px] max-h-20 overflow-auto">
          <pre className="whitespace-pre-wrap">
            {typeof result === 'string'
              ? (result.length > 200 ? result.slice(0, 200) + "..." : result)
              : JSON.stringify(result, null, 2).slice(0, 200)}
          </pre>
        </div>
      )}
    </div>
  );
};

/**
 * Simple markdown renderer for welcome messages
 * Handles basic formatting: bold (**text**), italic (*text*), bullet points (- item)
 */
const SimpleMarkdown: FC<{ text: string; className?: string }> = ({ text, className }) => {
  // Split into lines and process each
  const lines = text.split('\n');

  return (
    <div className={cn("text-sm leading-relaxed space-y-1", className)}>
      {lines.map((line, idx) => {
        // Skip empty lines but preserve spacing
        if (!line.trim()) {
          return <div key={idx} className="h-2" />;
        }

        // Horizontal rule
        if (line.trim() === '---') {
          return <hr key={idx} className="border-emerald-800/50 my-2" />;
        }

        // Bullet points
        if (line.trim().startsWith('- ')) {
          return (
            <div key={idx} className="flex gap-2 ml-2">
              <span className="text-emerald-500">•</span>
              <span>{processInlineFormatting(line.trim().slice(2))}</span>
            </div>
          );
        }

        // Italic line (like the "How can I help you" prompt)
        if (line.trim().startsWith('*') && line.trim().endsWith('*') && !line.trim().startsWith('**')) {
          return (
            <div key={idx} className="italic text-emerald-300 mt-2">
              {line.trim().slice(1, -1)}
            </div>
          );
        }

        // Regular line
        return <div key={idx}>{processInlineFormatting(line)}</div>;
      })}
    </div>
  );
};

/**
 * Process inline markdown: **bold** and *italic*
 */
function processInlineFormatting(text: string): ReactNode {
  const parts: ReactNode[] = [];
  let remaining = text;
  let keyIdx = 0;

  while (remaining.length > 0) {
    // Check for bold (**text**)
    const boldMatch = remaining.match(/\*\*(.+?)\*\*/);
    if (boldMatch && boldMatch.index !== undefined) {
      // Add text before the match
      if (boldMatch.index > 0) {
        parts.push(<span key={keyIdx++}>{remaining.slice(0, boldMatch.index)}</span>);
      }
      // Add bold text
      parts.push(<strong key={keyIdx++} className="font-semibold text-emerald-200">{boldMatch[1]}</strong>);
      remaining = remaining.slice(boldMatch.index + boldMatch[0].length);
      continue;
    }

    // Check for italic (*text*)
    const italicMatch = remaining.match(/\*(.+?)\*/);
    if (italicMatch && italicMatch.index !== undefined) {
      // Add text before the match
      if (italicMatch.index > 0) {
        parts.push(<span key={keyIdx++}>{remaining.slice(0, italicMatch.index)}</span>);
      }
      // Add italic text
      parts.push(<em key={keyIdx++} className="italic">{italicMatch[1]}</em>);
      remaining = remaining.slice(italicMatch.index + italicMatch[0].length);
      continue;
    }

    // No more matches, add remaining text
    parts.push(<span key={keyIdx++}>{remaining}</span>);
    break;
  }

  return parts.length > 0 ? <>{parts}</> : text;
}
